/* ------------------------------------------------------------------
   VideoEditorModal – crop, trim, filters, text, audio
   • Desktop / Tablet : fixed left sidebar
   • Mobile          : bottom icon bar + floating sub-panel
------------------------------------------------------------------ */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  XCircle, ArrowCounterclockwise, ArrowClockwise,
} from 'react-bootstrap-icons';
import {
  Modal, Button, Spinner, ButtonGroup,
  Alert, Badge, ProgressBar,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import axios from 'axios';

import Sidebar           from './Sidebar';
import CropPreview       from './CropPreview';
import TrimTimeline      from './TrimTimeline';
import AudioPanel        from './AudioPanel';
import MusicLibraryModal from './MusicLibraryModal';
import { aspectVal, toCdn } from './constants';
import { buildTimeline, startShotstackJob } from '../../../../../utils/shotstackBridge';
import { FILTERS } from './FiltersPanel';
import useIsMobile from '../../../../../hooks/useIsMobile';
import './VideoEditorModal.css';

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */
export default function VideoEditorModal({
  show = false,
  src  = '',
  onClose    = () => {},
  onComplete = () => {},
}) {
  /* ───────── layout helper ───────── */
  const isMobile = useIsMobile();

  /* ───────── editor state ───────── */
  const [preset, setPreset] = useState('keep');
  const [aspect, setAspect] = useState(undefined);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropPx, setCropPx] = useState(null);
  const [appliedCrop, setAppliedCrop] = useState(null);

  const [duration, setDuration]   = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd,   setTrimEnd]   = useState(0);

  const [filterId,  setFilterId]  = useState('normal');
  const [filterLvl, setFilterLvl] = useState(100);
  const [appliedFilter, setAppliedFilter] = useState({ id: 'normal', lvl: 100 });

  const [textItems, setTextItems] = useState([]);
  const [selectedTextId, setSelectedTextId] = useState(null);
  const [textFont, setTextFont]       = useState(48);
  const [textFontFam, setTextFontFam] = useState('Arial');
  const [textColor, setTextColor]     = useState('#ffffff');
  const [textBg,    setTextBg]        = useState('transparent');

  const [audio, setAudio]       = useState(null); // {src, volume, title}
  const [videoVol, setVideoVol] = useState(1);

  const [activeTool, setActiveTool] = useState('crop');
  const [previewT, setPreviewT]     = useState(0);
  const [dims, setDims]             = useState({ w: 0, h: 0 });

  const [busy,  setBusy]  = useState(false);
  const [error, setError] = useState(null);

  /* 🎵 Music-library modal */
  const [showLib, setShowLib] = useState(false);

  /* 🎞 Video-credit tracking */
  const [videoUsage, setVideoUsage]   = useState(null); // { used, limit, left }
  const [usageLoading, setUsageLoading] = useState(true);
  const [showNoCredits, setShowNoCredits] = useState(false);

  const previewVidRef = useRef(null);

  /* ------------------------------------------------------------------ */
  /*  Effects                                                           */
  /* ------------------------------------------------------------------ */
  useEffect(() => { setAspect(aspectVal(preset)) }, [preset]);   // block form (React 19 cleanup safety)

  /* fetch usage once */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get('/api/billing/usage', { withCredentials: true });
        setVideoUsage(data.video);
      } catch (e) {
        console.error('[Video usage] fetch failed', e);
      } finally {
        setUsageLoading(false);
      }
    })();
  }, []);

  /* load src metadata */
  useEffect(() => {
    if (!src) return;
    const v = document.createElement('video');
    v.src = toCdn(src);
    v.addEventListener('loadedmetadata', () => {
      setDuration(v.duration || 0);
      setTrimStart(0);
      setTrimEnd(v.duration || 0);
      setDims({ w: v.videoWidth, h: v.videoHeight });
    });
    return () => v.removeAttribute('src');
  }, [src]);

  /* hover-scrub preview */
  useEffect(() => {
    if (previewVidRef.current)
      previewVidRef.current.currentTime = Math.min(previewT, Math.max(0, duration - 0.05));
  }, [previewT, duration]);

  /* sync selected text props */
  useEffect(() => {
    if (!selectedTextId) return;
    setTextItems(prev =>
      prev.map(t =>
        t.id === selectedTextId
          ? { ...t, fontSize: textFont, fontFamily: textFontFam, color: textColor, bgColor: textBg }
          : t
      )
    );
  }, [selectedTextId, textFont, textFontFam, textColor, textBg]);

  /* ------------------------------------------------------------------ */
  /*  Helpers                                                           */
  /* ------------------------------------------------------------------ */
  const cropDirty   = cropPx && JSON.stringify(cropPx) !== JSON.stringify(appliedCrop);
  const filterDirty = filterId !== appliedFilter.id || filterLvl !== appliedFilter.lvl;

  const toPercentCrop = c => {
    if (!c || !dims.w || !dims.h) return null;
    const { x, y, width, height } = c;
    return {
      top   : (y * 100) / dims.h,
      left  : (x * 100) / dims.w,
      bottom: ((y + height) * 100) / dims.h,
      right : ((x + width) * 100) / dims.w,
    };
  };

  const previewFilter = activeTool === 'filters'
    ? { id: filterId, lvl: filterLvl }
    : appliedFilter;

  const baseCss   = FILTERS.find(f => f.id === previewFilter.id)?.css || '';
  const filterCss = previewFilter.lvl === 100 ? baseCss : `${baseCss} opacity(${previewFilter.lvl}%)`;

  const vLimit = videoUsage?.limit ?? 0;
  const vUsed  = videoUsage?.used  ?? 0;
  const vLeft  = videoUsage?.left  ?? Math.max(vLimit - vUsed, 0);
  const vPct   = vLimit ? Math.min(100, Math.round((vUsed / vLimit) * 100)) : 0;

  /* ------------------------------------------------------------------ */
  /*  Save → Shotstack                                                  */
  /* ------------------------------------------------------------------ */
  const handleSave = useCallback(async () => {
    try {
      setBusy(true); setError(null);

      const { timeline, output } = buildTimeline({
        src   : toCdn(src),
        crop  : toPercentCrop(appliedCrop || cropPx),
        preset,
        start : trimStart,
        length: trimEnd - trimStart,
        filter: appliedFilter,
        textItems,
        audio,
        videoVolume: videoVol,
      });

      const jobId      = await startShotstackJob({ timeline, output });
      const placeholder = toCdn(src);
      onComplete({ url: placeholder, jobId });
      onClose();
    } catch (err) {
      if (err?.response?.data?.error === 'video_limit_reached' ||
          err?.message?.includes('video_limit_reached')) {
        setShowNoCredits(true);
      } else {
        console.error(err);
        setError(err.message || 'Shotstack start failed');
      }
    } finally {
      setBusy(false);
    }
  }, [src, appliedCrop, cropPx, preset, trimStart, trimEnd,
      appliedFilter, textItems, audio, videoVol, onComplete, onClose]);

  /* ------------------------------------------------------------------ */
  /*  Render                                                            */
  /* ------------------------------------------------------------------ */
  if (!show) return null;

  return (
    <>
      {/* ───────────────── MAIN EDITOR MODAL ───────────────── */}
      <Modal
        show={show}
        onHide={onClose}
        size="xl"
        centered
        backdrop="static"
        fullscreen="sm-down"
        dialogClassName="video-editor-modal modal-xl"
      >

        {/* ---------- Top bar ---------- */}
        <div className="top-bar d-flex align-items-center px-2 py-2 border-bottom">
          <Button size="sm" variant="primary" onClick={handleSave} disabled={busy}>
            {busy ? <Spinner size="sm" animation="border" /> : 'Save'}
          </Button>

          {/* desktop-only duration read-out */}
          {!isMobile && (
            <div className="mx-auto small text-muted">
              {duration ? `Selected ${(trimEnd - trimStart).toFixed(1)} s / ${duration.toFixed(1)} s` : ''}
            </div>
          )}

          {/* credit badge – now ALWAYS visible */}
          <div
            className={`d-flex flex-column ${isMobile ? 'ms-auto me-1' : 'align-items-end me-2'}`}
            style={isMobile ? {} : { minWidth: 110 }}
          >
            {usageLoading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <>
                <Badge bg="info" className={isMobile ? '' : 'mb-1'}>{vLeft}&nbsp;Credits&nbsp;Left</Badge>
                {/* progress bar only on md-up */}
                {!isMobile && (
                  <ProgressBar
                    now={vPct}
                    style={{ width: 110, height: 6 }}
                    animated
                    variant={vPct > 75 ? 'danger' : vPct > 50 ? 'warning' : 'success'}
                  />
                )}
              </>
            )}
          </div>

          {/* undo / redo hidden on xs to avoid overflow */}
          <ButtonGroup size="sm" className="me-2 d-none d-md-flex">
            <Button variant="light"><ArrowCounterclockwise /></Button>
            <Button variant="light"><ArrowClockwise /></Button>
          </ButtonGroup>

          <Button variant="light" size="sm" className="ms-1" onClick={onClose}>
            <XCircle />
          </Button>
        </div>

        {/* ---------- Body ---------- */}
        <Modal.Body className="p-0" style={{ height: '70vh', overflowY: 'auto' }}>
          <div className="d-flex h-100">

            {/* ----- Sidebar (desktop) ----- */}
            {!isMobile && (
              <Sidebar
                active={activeTool}
                onChange={setActiveTool}
                preset={preset} setPreset={setPreset}
                zoom={zoom}     setZoom={setZoom}
                showApply={cropDirty} onApply={() => { setAppliedCrop(cropPx); setActiveTool('trim'); }}
                filterId={filterId} setFilterId={setFilterId}
                filterLevel={filterLvl} setFilterLevel={setFilterLvl}
                filterDirty={filterDirty} onFilterApply={() => { setAppliedFilter({ id: filterId, lvl: filterLvl }); setActiveTool(''); }}
                textProps={{
                  fontSize: textFont,   setFontSize: setTextFont,
                  fontFamily: textFontFam, setFontFamily: setTextFontFam,
                  color: textColor,        setColor: setTextColor,
                  bgColor: textBg,         setBgColor: setTextBg,
                  onAdd: () => {
                    const id = uuid();
                    setTextItems(p => [
                      ...p,
                      {
                        id,
                        html: 'Edit me',
                        x: 225, y: 225,
                        fontSize: textFont,
                        fontFamily: textFontFam,
                        color: textColor,
                        bgColor: textBg,
                        start: 0,
                        length: duration,
                      },
                    ]);
                    setSelectedTextId(id);
                  },
                }}
              />
            )}

            {/* ----- Main preview column ----- */}
            <div className="flex-grow-1 d-flex flex-column bg-light">
              {activeTool === 'audio' ? (
                /* ===== AUDIO TOOL ===== */
                <div className={isMobile ? 'd-flex flex-column' : 'd-flex flex-grow-1'}>
                  {/* preview */}
                  <div className={isMobile
                    ? 'position-relative d-flex justify-content-center align-items-start p-4'
                    : 'flex-grow-1 position-relative d-flex justify-content-center align-items-start p-4'}>
                    <CropPreview
                      ref={previewVidRef}
                      src={src}
                      editing={false}
                      crop={crop}
                      zoom={zoom}
                      aspect={aspect}
                      appliedCrop={appliedCrop}
                      dims={dims}
                      trimStart={trimStart}
                      trimEnd={trimEnd}
                      filterCss={filterCss}
                      textItems={textItems}
                      setTextItems={setTextItems}
                      selectedTextId={selectedTextId}
                      setSelectedTextId={setSelectedTextId}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={(_, px) => setCropPx(px)}
                    />
                  </div>

                  {/* audio controls */}
                  <div
                    className={isMobile ? '' : 'border-start'}
                    style={{
                      width    : isMobile ? '100%' : 320,
                      maxWidth : isMobile ? '100%' : '40%',
                      borderTop: isMobile ? '1px solid #dee2e6' : undefined,
                    }}
                  >
                    <AudioPanel
                      videoRef={previewVidRef}
                      audio={audio}
                      setAudio={setAudio}
                      videoVolume={videoVol}
                      setVideoVolume={setVideoVol}
                      onOpenLibrary={() => setShowLib(true)}
                    />
                  </div>
                </div>
              ) : (
                /* ===== OTHER TOOLS ===== */
                <div className="position-relative d-flex justify-content-center align-items-start p-4 flex-grow-1">
                  <CropPreview
                    ref={previewVidRef}
                    src={src}
                    editing={activeTool === 'crop'}
                    crop={crop}
                    zoom={zoom}
                    aspect={aspect}
                    appliedCrop={appliedCrop}
                    dims={dims}
                    trimStart={trimStart}
                    trimEnd={trimEnd}
                    filterCss={filterCss}
                    textItems={textItems}
                    setTextItems={setTextItems}
                    selectedTextId={selectedTextId}
                    setSelectedTextId={setSelectedTextId}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={(_, px) => setCropPx(px)}
                  />

                  {activeTool === 'trim' && duration > 0 && (
                    <TrimTimeline
                      src={toCdn(src)}
                      duration={duration}
                      onHover={t => setPreviewT(t)}
                      onConfirm={(s, e) => {
                        setTrimStart(s);
                        setTrimEnd(e);
                        setActiveTool('');
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </Modal.Body>

        {error && (
          <Modal.Footer>
            <Alert variant="danger" className="mb-0 w-100">{error}</Alert>
          </Modal.Footer>
        )}
      </Modal>

      {/* ─────────── Mobile bottom bar + sub-panel ─────────── */}
      {isMobile && (
        <Sidebar
          active={activeTool}
          onChange={setActiveTool}
          preset={preset} setPreset={setPreset}
          zoom={zoom}     setZoom={setZoom}
          showApply={cropDirty} onApply={() => { setAppliedCrop(cropPx); setActiveTool('trim'); }}
          filterId={filterId} setFilterId={setFilterId}
          filterLevel={filterLvl} setFilterLevel={setFilterLvl}
          filterDirty={filterDirty} onFilterApply={() => { setAppliedFilter({ id: filterId, lvl: filterLvl }); }}
          textProps={{
            fontSize: textFont,   setFontSize: setTextFont,
            fontFamily: textFontFam, setFontFamily: setTextFontFam,
            color: textColor,        setColor: setTextColor,
            bgColor: textBg,         setBgColor: setTextBg,
            onAdd: () => {
              const id = uuid();
              setTextItems(p => [
                ...p,
                {
                  id,
                  html: 'Edit me',
                  x: 225, y: 225,
                  fontSize: textFont,
                  fontFamily: textFontFam,
                  color: textColor,
                  bgColor: textBg,
                  start: 0,
                  length: duration,
                },
              ]);
              setSelectedTextId(id);
            },
          }}
          mobileHorizontal
        />
      )}

      {/* ───────────────── Out-of-credits popup ───────────────── */}
      <Modal show={showNoCredits} onHide={() => setShowNoCredits(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Out of Video Credits</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>You’ve used all of your video-render credits for this billing cycle.</p>
          <p className="mb-0">Buy a one-time pack now or upgrade your plan to continue rendering videos.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button as={Link} to="/dashboard/video-credits" variant="primary">Buy Credits</Button>
          <Button variant="outline-secondary" onClick={() => setShowNoCredits(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* 🎵 Music library */}
      <MusicLibraryModal
        show={showLib}
        onHide={() => setShowLib(false)}
        onSelect={track => setAudio({ src: track.mp3, volume: 1, title: track.title })}
      />
    </>
  );
}

/* ------------------------------------------------------------------ */
VideoEditorModal.propTypes = {
  show      : PropTypes.bool,
  src       : PropTypes.string,
  onClose   : PropTypes.func,
  onComplete: PropTypes.func,
};
